'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FilterBarProps {
  filters: {
    type: string;
    location: string;
    style: string;
    budget: string;
    search: string;
  };
  onFilterChange: (filterType: string, value: string) => void;
  onClearFilters: () => void;
}

const FilterBar = ({ filters, onFilterChange, onClearFilters }: FilterBarProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <div className="bg-white rounded-2xl border border-zinc-200 p-6 mb-8">
        <div className="h-10 bg-zinc-100 animate-pulse rounded-full" />
      </div>
    );
  }

  const typeOptions = [
    { value: '', label: 'All Types' },
    { value: 'apartment', label: 'Apartment' },
    { value: 'villa', label: 'Villa' },
    { value: 'penthouse', label: 'Penthouse' },
    { value: 'bungalow', label: 'Bungalow' },
  ];

  const locationOptions = [
    { value: '', label: 'All Locations' },
    { value: 'bangalore', label: 'Bangalore' },
    { value: 'mumbai', label: 'Mumbai' },
    { value: 'delhi', label: 'Delhi' },
    { value: 'hyderabad', label: 'Hyderabad' },
    { value: 'pune', label: 'Pune' },
  ];

  const styleOptions = [
    { value: '', label: 'All Styles' },
    { value: 'contemporary', label: 'Contemporary' },
    { value: 'traditional', label: 'Traditional' },
    { value: 'minimalist', label: 'Minimalist' },
    { value: 'fusion', label: 'Fusion' },
    { value: 'boho', label: 'Boho' },
    { value: 'luxury', label: 'Luxury' },
  ];

  const budgetOptions = [
    { value: '', label: 'All Budgets' },
    { value: '10-20', label: '₹10-20 Lakhs' },
    { value: '20-40', label: '₹20-40 Lakhs' },
    { value: '40-60', label: '₹40-60 Lakhs' },
    { value: '60+', label: '₹60+ Lakhs' },
  ];

  const hasActiveFilters = filters.type || filters.location || filters.style || filters.budget || filters.search;

  return (
    <div className="bg-white rounded-2xl border border-zinc-200 p-4 md:p-6 mb-6 md:mb-8">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-black/5 border border-black/10">
          <Icon name="AdjustmentsHorizontalIcon" size={16} className="text-black/70" />
          <span className="text-xs font-medium text-black/90 tracking-widest uppercase">
            Filter Projects
          </span>
        </div>
        <button
          onClick={() => setIsMobileFilterOpen(!isMobileFilterOpen)}
          className="lg:hidden p-2 text-black hover:text-[#1B5E4F] transition-colors duration-200"
        >
          <Icon name={isMobileFilterOpen ? 'XMarkIcon' : 'AdjustmentsHorizontalIcon'} size={24} />
        </button>
      </div>

      <div className={`space-y-4 ${isMobileFilterOpen ? 'block' : 'hidden lg:block'}`}>
        <div className="relative">
          <Icon name="MagnifyingGlassIcon" size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            type="text"
            placeholder="Search projects by name or description..."
            value={filters.search}
            onChange={(e) => onFilterChange('search', e.target.value)}
            className="w-full pl-11 pr-4 py-3 bg-zinc-50 border border-zinc-200 rounded-full text-sm text-black placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black/20 transition-all duration-200"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          <div>
            <label className="block text-xs font-medium text-black/70 mb-2 uppercase tracking-wider">
              Property Type
            </label>
            <select
              value={filters.type}
              onChange={(e) => onFilterChange('type', e.target.value)}
              className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-xl text-sm text-black focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black/20 transition-all duration-200"
            >
              {typeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-black/70 mb-2 uppercase tracking-wider">
              Location
            </label>
            <select
              value={filters.location}
              onChange={(e) => onFilterChange('location', e.target.value)}
              className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-xl text-sm text-black focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black/20 transition-all duration-200"
            >
              {locationOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-black/70 mb-2 uppercase tracking-wider">
              Design Style
            </label>
            <select
              value={filters.style}
              onChange={(e) => onFilterChange('style', e.target.value)}
              className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-xl text-sm text-black focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black/20 transition-all duration-200"
            >
              {styleOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-black/70 mb-2 uppercase tracking-wider">
              Budget Range
            </label>
            <select
              value={filters.budget}
              onChange={(e) => onFilterChange('budget', e.target.value)}
              className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-xl text-sm text-black focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black/20 transition-all duration-200"
            >
              {budgetOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {hasActiveFilters && (
          <div className="flex items-center justify-between pt-4 border-t border-zinc-200">
            <span className="text-sm text-zinc-500 font-normal">
              Active filters applied
            </span>
            <button
              onClick={onClearFilters}
              className="inline-flex items-center gap-2 px-4 py-2 text-white bg-black hover:bg-black/80 font-medium text-sm rounded-full transition-all duration-300 active:scale-95 min-h-[44px]"
            >
              <Icon name="XMarkIcon" size={14} />
              <span>Clear All</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterBar;